import { IFsMap } from '@lib/geo/core';
import { Evented, SnLonLat } from '@lib/geo/helpers';
import { SnBadgeConfig } from '@lib/ui/badge';

import { MapMarkerIcon } from '../../core/marker/icon-marker-options';
import { IMapMarkerStyleOptions } from '../../core/marker/marker-options';
import { FsMapPopup, IFsMapPopupEvents } from '../../core/popup';
import { IMarkerOptions } from './marker-options.interface';

// Base marker interface with core properties
export interface IMarkerBase {
  // Core identification
  readonly id: string;

  // Position and geometry
  position: SnLonLat;
  visible: boolean;

  // Basic appearance
  icon?: MapMarkerIcon;
  color?: string;
  size?: string;
  rotation?: number;

  // Behavior and interaction
  draggable: boolean;
  clickable: boolean;

  // Content and labels - title is required for accessibility
  title: string; // Required for automatic aria-label generation
  content?: HTMLElement;

  // Z-index and layering
  zIndex?: number;
}

// Extended marker interface with additional facade-specific properties
export interface IMarker<TMap extends IFsMap> extends IMarkerBase {
  // Styling and appearance
  styles?: IMapMarkerStyleOptions;
  customClass?: string;
  customStyle?: Record<string, string>;

  // Labels and content
  iconLabel?: IMarkerIconLabel;
  tooltip?: string;

  // Data and attributes
  attributes?: Record<string, unknown>;
  metadata?: IMarkerMetadata;

  // Configuration
  options: IMarkerOptions;

  // UI elements
  badge?: SnBadgeConfig;
  popup?: FsMapPopup<TMap>;

  // State management
  selected: boolean;
  highlighted: boolean;
  focused: boolean;

  // Performance and optimization
  priority: 'low' | 'normal' | 'high';
  lazyLoaded: boolean;

  // Event handling state
  eventHandlers: IMarkerEventHandlers;

  // Computed accessibility properties (auto-generated from other fields)
  readonly ariaLabel: string; // Auto-generated from title and other properties
  readonly role: string; // Auto-generated based on clickable/draggable state
  readonly tabIndex: number; // Auto-generated based on clickable state

  // Lifecycle state
  created: Date;
  lastModified: Date;
  version: number;
}

// Supporting interfaces
export interface IMarkerIconLabel {
  text: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
  offset?: {
    x: number;
    y: number;
  };
  style?: Record<string, string>;
}

export interface IMarkerMetadata {
  entityType?: string;
  entityId?: string;
  source?: string;
  tags?: string[];
  category?: string;
  priority?: number;
  customData?: Record<string, unknown>;

  // Animation and movement properties
  followMode?: boolean;
  destination?: SnLonLat;
  animationSeconds?: number;

  // Visual and styling properties
  type?: string;
  label?: IMarkerIconLabel;
  pulseAnimation?: {
    active: boolean;
    colors?: string[];
    delay?: number;
    fontSet?: string;
    fontIcon?: string;
    interval?: number;
    color?: string;
  };

  // Location and content properties
  address?: string;
  feature?: unknown;
  iconAnchor?: SnLonLat;

  // Status and badge properties
  statusBadge?: {
    status?: string;
    position?: Record<string, unknown>;
    color?: string;
    text?: string;
  };
}

export interface IMarkerEventHandlers {
  // Mouse and touch events
  click: boolean;
  doubleClick: boolean;
  rightClick: boolean;
  mouseOver: boolean;
  mouseOut: boolean;
  mouseEnter: boolean;
  mouseLeave: boolean;

  // Drag events
  dragStart: boolean;
  drag: boolean;
  dragEnd: boolean;

  // Focus events
  focus: boolean;
  blur: boolean;

  // State change events
  positionChange: boolean;
  titleChange: boolean;
  visibilityChange: boolean;
  selectionChange: boolean;
  iconChange: boolean;
  styleChange: boolean;
}

// Default event handlers - all events disabled by default
export const DEFAULT_MARKER_EVENT_HANDLERS: IMarkerEventHandlers = {
  // Mouse and touch events
  click: false,
  doubleClick: false,
  rightClick: false,
  mouseOver: false,
  mouseOut: false,
  mouseEnter: false,
  mouseLeave: false,

  // Drag events
  dragStart: false,
  drag: false,
  dragEnd: false,

  // Focus events
  focus: false,
  blur: false,

  // State change events
  positionChange: false,
  titleChange: false,
  visibilityChange: false,
  selectionChange: false,
  iconChange: false,
  styleChange: false,
};

// Helper type for creating custom event handler configurations
export type IMarkerEventHandlersConfig = Partial<IMarkerEventHandlers>;

// Marker state interface for tracking changes
export interface IMarkerState<TMap extends IFsMap> {
  marker: IMarker<TMap>;
  previousState?: Partial<IMarker<TMap>>;
  changedProperties: (keyof IMarker<TMap>)[];
  lastChange: Date;
}

// Marker collection interface for managing multiple markers
export interface IMarkerCollection<TMap extends IFsMap> {
  markers: Map<string, IMarker<TMap>>;
  selected: Set<string>;
  visible: Set<string>;
  filtered: Set<string>;
  lastUpdated: Date;
}

// Marker query interface for searching and filtering
export interface IMarkerQuery<TMap extends IFsMap> {
  ids?: string[];
  entityTypes?: string[];
  tags?: string[];
  visible?: boolean;
  selected?: boolean;
  bounds?: {
    north: number;
    south: number;
    east: number;
    west: number;
  };
  customFilter?: (marker: IMarker<TMap>) => boolean;
}

// Accessibility helper interface for automatic generation
export interface IMarkerAccessibility {
  ariaLabel: string;
  role: string;
  tabIndex: number;
}

// Default accessibility generator function
export const DEFAULT_MARKER_ACCESSIBILITY: <TMap extends IFsMap>(
  marker: IMarker<TMap>,
) => IMarkerAccessibility = (marker): IMarkerAccessibility => {
  // Generate descriptive aria-label from title and other properties
  let ariaLabel = marker.title;

  if (marker.metadata?.entityType) {
    ariaLabel += ` (${marker.metadata.entityType})`;
  }

  if (marker.selected) {
    ariaLabel += ', selected';
  }

  if (marker.highlighted) {
    ariaLabel += ', highlighted';
  }

  if (marker.draggable) {
    ariaLabel += ', draggable';
  }

  // Generate role based on interaction capabilities
  let role = 'img'; // Default for non-interactive markers
  if (marker.clickable) {
    role = 'button';
  } else if (marker.draggable) {
    role = 'img';
  }

  // Generate tabIndex based on clickability
  const tabIndex = marker.clickable ? 0 : -1;

  return {
    ariaLabel,
    role,
    tabIndex,
  };
};
